import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import CreateAssignment from './CreateAssignment';
import ManageExams from './ManageExams';
import PublishResults from './PublishResults';
import ReportCard from './ReportCard';
import LeaveRequests from './LeaveRequests';
import CreateHomework from './CreateHomework';
import StudyMaterials from './StudyMaterials';
import ConfigureTerms from './ConfigureTerms';
import ConfigureSubjects from './ConfigureSubjects';
import ReassignTeacher from './ReassignTeacher';

interface AcademicsModuleProps {
  domain: string;
  onBack: () => void;
}

const AcademicsModule: React.FC<AcademicsModuleProps> = ({ domain, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>('assignments');

  // Update active tab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/homework')) setActiveTab('homework');
    else if (path.includes('/exams')) setActiveTab('exams');
    else if (path.includes('/report-card')) setActiveTab('report-card');
    else if (path.includes('/results')) setActiveTab('results');
    else if (path.includes('/materials')) setActiveTab('materials');
    else if (path.includes('/leave')) setActiveTab('leave');
    else if (path.includes('/configure-terms')) setActiveTab('configure-terms');
    else if (path.includes('/configure-subjects')) setActiveTab('configure-subjects');
    else if (path.includes('/reassign')) setActiveTab('reassign');
    else setActiveTab('assignments');
  }, [location.pathname]);

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div className="bg-primary-custom text-white p-3 shadow-sm">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <h4 className="mb-0">
            <i className="bi bi-book me-2"></i>
            Academics
          </h4>
          <button className="btn btn-light btn-sm" onClick={onBack}>
            <i className="bi bi-arrow-left me-2"></i>
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow-sm">
        <div className="container-fluid">
          <ul className="nav nav-tabs border-0">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'assignments' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/academics')}
              >
                <i className="bi bi-file-earmark-text me-2"></i>
                Assignments
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'homework' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/academics/homework')}
              >
                <i className="bi bi-journal-text me-2"></i>
                Homework
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'exams' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/academics/exams')}
              >
                <i className="bi bi-clipboard-check me-2"></i>
                Exams
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'results' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/academics/results')}
              >
                <i className="bi bi-graph-up me-2"></i>
                Results
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'report-card' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/academics/report-card')}
              >
                <i className="bi bi-file-earmark-ruled me-2"></i>
                Report Card
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'materials' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/academics/materials')}
              >
                <i className="bi bi-folder me-2"></i>
                Study Materials
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'leave' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/academics/leave')}
              >
                <i className="bi bi-calendar-x me-2"></i>
                Leave Requests
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'configure-terms' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/academics/configure-terms')}
              >
                <i className="bi bi-gear me-2"></i>
                Terms
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'configure-subjects' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/academics/configure-subjects')}
              >
                <i className="bi bi-gear-fill me-2"></i>
                Subjects
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'reassign' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/academics/reassign')}
              >
                <i className="bi bi-arrow-left-right me-2"></i>
                Reassign Teacher
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Content Area */}
      <div className="container-fluid py-4">
        <Routes>
          <Route index element={<CreateAssignment domain={domain} />} />
          <Route path="homework" element={<CreateHomework domain={domain} />} />
          <Route path="exams" element={<ManageExams domain={domain} />} />
          <Route path="results" element={<PublishResults domain={domain} />} />
          <Route path="report-card" element={<ReportCard domain={domain} />} />
          <Route path="materials" element={<StudyMaterials domain={domain} />} />
          <Route path="leave" element={<LeaveRequests domain={domain} />} />
          <Route path="configure-terms" element={<ConfigureTerms domain={domain} />} />
          <Route path="configure-subjects" element={<ConfigureSubjects domain={domain} />} />
          <Route path="reassign" element={<ReassignTeacher domain={domain} />} />
        </Routes>
      </div>
    </div>
  );
};

export default AcademicsModule;
